/*
 * 文件职责 / File Purpose
 * - 中文：读取分型预计算结果与状态，维护短期浏览器缓存，并封装单标的/批量预计算任务触发接口。
 * - English: Reads fractal-precompute results and status, maintains a short-lived browser cache, and wraps single-symbol/batch precompute task triggers.
 *
 * 算法与任务边界 / Algorithm and Job Boundary
 * - 中文：本 service 不包含分型算法；所有结果和计算任务由 BFF 权威实现。缓存只保存响应副本，触发成功后必须清理相关键。
 * - English: This service contains no fractal algorithm; the BFF owns all results and compute jobs. The cache stores response copies only and relevant keys are cleared after successful triggers.
 */
/**
 * 分型预计算 API 服务
 * 封装与 BFF 层的通信
 *
 * @author FractalAI
 * @date 2026-01-05
 */

/**
 * 分型预计算 API 类
 */
const FRACTAL_API_CACHE_VERSION = 'effective_wave_marker_v3';

class FractalPrecomputedAPI {
  constructor(baseURL = '/api/vchart/fractal') {
    this.baseURL = baseURL;
    this.cache = new Map(); // 简单的内存缓存
    this.cacheExpiry = 5 * 60 * 1000; // 缓存5分钟
  }

  /**
   * 获取预计算的分型数据
   * @param {string} symbol - 股票代码 (如: 600519.SH)
   * @param {string} resolution - 周期 (1/5/30/60/D)
   * @param {number|null} from - 开始时间戳（毫秒）
   * @param {number|null} to - 结束时间戳（毫秒）
   * @returns {Promise<Object>} API响应结果
   */
  async getPrecomputedData(symbol, resolution, from = null, to = null) {
    try {
      // 生成缓存键
      const cacheKey = `${FRACTAL_API_CACHE_VERSION}_${symbol}_${resolution}_${from}_${to}`;

      // 检查缓存
      const cached = this._getFromCache(cacheKey);
      if (cached) {
        console.log(`[FractalAPI] ✅ 使用缓存数据: ${symbol} ${resolution}`);
        return {
          success: true,
          data: cached,
          source: 'cache',
          cached: true
        };
      }

      // 构建查询参数
      const params = new URLSearchParams({ symbol, resolution });
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      console.log(`[FractalAPI] 🔍 请求预计算数据: ${symbol} ${resolution}`);

      const response = await fetch(`${this.baseURL}/precomputed?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // 缓存成功的响应
        this._setToCache(cacheKey, data);

        console.log(`[FractalAPI] ✅ 获取预计算数据成功: ${data.fractals?.length || 0} 个分型点`);

        return {
          success: true,
          data: data,
          source: 'precomputed',
          cached: false
        };
      } else {
        console.warn(`[FractalAPI] ⚠️ 预计算数据不可用: ${data.errmsg}`);
        return {
          success: false,
          error: data.errmsg || '数据不可用',
          source: 'empty',
          cached: false
        };
      }
    } catch (error) {
      console.error('[FractalAPI] ❌ 获取预计算数据失败:', error);
      return {
        success: false,
        error: error.message,
        source: 'error',
        cached: false
      };
    }
  }

  /**
   * 获取计算状态
   * @param {string|null} symbol - 股票代码（可选）
   * @param {string|null} resolution - 周期（可选）
   * @returns {Promise<Object>} 状态信息
   */
  async getStatus(symbol = null, resolution = null) {
    try {
      const params = new URLSearchParams();
      if (symbol) params.append('symbol', symbol);
      if (resolution) params.append('resolution', resolution);

      const url = `${this.baseURL}/precomputed/status${params.toString() ? '?' + params : ''}`;

      console.log('[FractalAPI] 🔍 查询计算状态');

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log('[FractalAPI] ✅ 状态查询成功');
        return data;
      } else {
        console.warn('[FractalAPI] ⚠️ 状态查询失败:', data.errmsg);
        return data;
      }
    } catch (error) {
      console.error('[FractalAPI] ❌ 状态查询失败:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  /**
   * 触发单个股票的预计算
   * @param {string} symbol - 股票代码
   * @param {string} resolution - 周期
   * @returns {Promise<Object>} 触发结果
   */
  async triggerCompute(symbol, resolution) {
    try {
      console.log(`[FractalAPI] 🚀 触发预计算: ${symbol} ${resolution}`);

      const response = await fetch(`${this.baseURL}/precomputed/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ symbol, resolution })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log(`[FractalAPI] ✅ 触发成功, 任务ID: ${data.data?.taskId}`);

        // 清除相关缓存
        this._clearCacheForSymbol(symbol, resolution);

        return data;
      } else {
        console.warn('[FractalAPI] ⚠️ 触发失败:', data.errmsg);
        return data;
      }
    } catch (error) {
      console.error('[FractalAPI] ❌ 触发失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 批量触发预计算
   * @param {Object} options - 批量选项
   * @param {Array<string>} options.symbols - 股票代码数组
   * @param {string} options.resolution - 周期
   * @param {number} options.limit - 限制数量
   * @returns {Promise<Object>} 批量触发结果
   */
  async batchCompute(options = {}) {
    try {
      const {
        symbols,
        resolution = 'D',
        assetType,
        type,
        limit,
        full,
        runtimeProfile
      } = options;

      console.log(`[FractalAPI] 🚀 批量触发预计算: ${symbols?.length || 'all'} 个股票`);

      const response = await fetch(`${this.baseURL}/precomputed/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          symbols,
          resolution,
          assetType,
          type,
          limit,
          full,
          runtimeProfile
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log(`[FractalAPI] ✅ 批量触发成功, 任务ID: ${data.data?.taskId}`);

        // 清除所有缓存
        this.clearCache();

        return data;
      } else {
        console.warn('[FractalAPI] ⚠️ 批量触发失败:', data.errmsg);
        return data;
      }
    } catch (error) {
      console.error('[FractalAPI] ❌ 批量触发失败:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 从缓存获取数据
   * @private
   */
  _getFromCache(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.cacheExpiry) {
      // 缓存过期，删除
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * 设置缓存
   * @private
   */
  _setToCache(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  /**
   * 清除特定股票的缓存
   * @private
   */
  _clearCacheForSymbol(symbol, resolution) {
    const keys = Array.from(this.cache.keys());
    const prefix = `${symbol}_${resolution}`;

    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    });
  }

  /**
   * 清除所有缓存
   */
  clearCache() {
    console.log('[FractalAPI] 🗑️ 清除所有缓存');
    this.cache.clear();
  }

  /**
   * 获取缓存统计
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      expiry: this.cacheExpiry
    };
  }
}

// 创建单例实例
const fractalApi = new FractalPrecomputedAPI();

// 导出
export default fractalApi;
export { FractalPrecomputedAPI };
